import { useMemo } from 'react'
import { usePublicClient, useWalletClient, useChainId } from 'wagmi'
import { getContract, type Address } from 'viem'
import { useActiveWeb3React } from './index'
import { V2_CONTRACTS, classic } from '../wagmi'
import { ECNS_REGISTRY_ADDRESS } from '../constants'

// ABIs - these will need to be updated to viem format
import ERC20_ABI from '../constants/abis/erc20.json'
import WETC_ABI from '../constants/abis/wetc.json'
import PAIR_ABI from '../constants/abis/pair.json'
import ECNS_REGISTRY_ABI from '../constants/abis/ecns-registry.json'
import ECNS_RESOLVER_ABI from '../constants/abis/ecns-resolver.json'
import { MULTICALL_ABI } from '../constants/multicall'

// Contract type is just the return of getContract (opaque type)
export type ContractType = ReturnType<typeof getContract>

/**
 * Returns a contract instance using viem
 */
function useContract<TAbi extends readonly unknown[]>(
  address: Address | undefined,
  abi: TAbi,
  withSignerIfPossible = true
) {
  const chainId = useChainId()
  // Get public client for current chain, default to ETC mainnet
  const effectiveChainId = chainId || classic.id
  const publicClient = usePublicClient({ chainId: effectiveChainId })
  const { data: walletClient } = useWalletClient()

  return useMemo(() => {
    if (!address || !publicClient) return null

    try {
      // viem getContract expects specific client types
      // Use type assertions for the complex client union types
      if (withSignerIfPossible && walletClient) {
        return getContract({
          address,
          abi,
          client: { public: publicClient, wallet: walletClient } as never,
        })
      }
      return getContract({
        address,
        abi,
        client: publicClient as never,
      })
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [address, abi, publicClient, walletClient, withSignerIfPossible])
}

export function useTokenContract(tokenAddress?: Address, withSignerIfPossible?: boolean) {
  return useContract(tokenAddress, ERC20_ABI as readonly unknown[], withSignerIfPossible)
}

export function useWETCContract(withSignerIfPossible?: boolean) {
  const { chainId } = useActiveWeb3React()
  const wetcAddress = chainId ? V2_CONTRACTS[chainId as 61 | 63]?.WETC : undefined
  return useContract(wetcAddress as Address | undefined, WETC_ABI as readonly unknown[], withSignerIfPossible)
}

export function usePairContract(pairAddress?: Address, withSignerIfPossible?: boolean) {
  return useContract(pairAddress, PAIR_ABI as readonly unknown[], withSignerIfPossible)
}

export function useMulticallContract() {
  const { chainId } = useActiveWeb3React()
  const wagmiChainId = useChainId()
  // Use wagmi chainId as fallback, then default to ETC mainnet
  const effectiveChainId = chainId || wagmiChainId || classic.id
  const multicallAddress = V2_CONTRACTS[effectiveChainId as 61 | 63]?.multicall
  return useContract(multicallAddress as Address | undefined, MULTICALL_ABI as readonly unknown[], false)
}

// Export the base hook for custom contracts
export { useContract }

// Stub exports for features not available on ETC
// These return null since the features don't exist on ETC

// Bytes32 token contract (some old tokens use bytes32 for symbol/name)
export function useBytes32TokenContract(tokenAddress?: Address, withSignerIfPossible?: boolean) {
  return useContract(tokenAddress, ERC20_ABI as readonly unknown[], withSignerIfPossible)
}

// ECNS contracts for Ethereum Classic Name Service
// Uses ECNS on Mordor testnet, mainnet pending deployment
export function useENSRegistrarContract(withSignerIfPossible?: boolean) {
  const { chainId } = useActiveWeb3React()
  const ecnsAddress = chainId ? ECNS_REGISTRY_ADDRESS[chainId] : undefined
  return useContract(ecnsAddress as Address | undefined, ECNS_REGISTRY_ABI as readonly unknown[], withSignerIfPossible)
}

export function useENSResolverContract(address?: Address, withSignerIfPossible?: boolean) {
  return useContract(address, ECNS_RESOLVER_ABI as readonly unknown[], withSignerIfPossible)
}

// V1 exchange - ETCswap V1 was never deployed
export function useV1ExchangeContract(_address?: Address, _withSignerIfPossible?: boolean) {
  return null
}
